import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DataSummaryComponent, DataSummary } from '../data-summary/data-summary.component';
import { LayoutService } from '../../services/layout.service';
import { DataService } from '../../services/data.service';
import { ChartProcessingService } from '../../services/chart-processing.service';
import { AnomalyListDialogComponent } from '../anomaly-list-dialog/anomaly-list-dialog.component';
import { SelectedDataDialogComponent } from '../selected-data-dialog/selected-data-dialog.component';
import { VisualiseDialogComponent } from '../visualise-dialog/visualise-dialog.component';
import { InteractiveChartComponent } from '../interactive-chart/interactive-chart.component';
import { ChartDataResponse } from '../../models/chart';
import { FileUploadService } from '../../services/file-upload.service';

@Component({
  selector: 'app-anomaly-detection-results',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTooltipModule,
    DataSummaryComponent,
    InteractiveChartComponent
  ],
  templateUrl: './anomaly-detection-results.component.html',
  styleUrls: ['./anomaly-detection-results.component.scss']
})
export class AnomalyDetectionResultsComponent implements OnInit, OnDestroy {
  @Input() results: any = null;
  @Input() loading = false;
  @Input() runDate: Date | null = null;
  @Input() dataSourceId: string | null = null; // For datasource-based charts
  @Input() uploadedFileData: any = null; // For uploaded file-based charts
  @Input() availableColumns: Array<{name: string, type: string}> = []; // Available columns metadata

  // Data summary
  dataSummary: DataSummary | null = null;

  // Track expanded columns
  private expandedColumns = new Set<string>();
  // Track table-level expanded state
  private tableLevelExpanded = true;
  
  // Scatter plot state management
  private columnChartsLoaded = new Set<string>(); // Tracks which columns have loaded charts
  columnChartData = new Map<string, ChartDataResponse>(); // Chart data per column
  columnChartLoading = new Map<string, boolean>(); // Loading state per column
  columnChartErrors = new Map<string, string>(); // Error messages per column
  selectedXColumns = new Map<string, string>(); // Selected X-axis column per Y column
  
  // View mode subscription
  private viewModeSubscription: Subscription | null = null;

  constructor(
    public layoutService: LayoutService,
    private dataService: DataService,
    private chartProcessingService: ChartProcessingService,
    private dialog: MatDialog,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // Initialize only the first column as expanded by default
    if (this.hasColumnLevelResults()) {
      const columns = this.getColumnNames();
      this.expandedColumns = columns.length > 0 ? new Set([columns[0]]) : new Set();
    }
    
    // Generate data summary
    this.generateDataSummary();
    
    // Subscribe to view mode changes
    this.viewModeSubscription = this.layoutService.viewMode$.subscribe(() => {
      this.generateDataSummary();
    });
  }

  ngOnDestroy(): void {
    if (this.viewModeSubscription) {
      this.viewModeSubscription.unsubscribe();
    }
  }

  get isAdvancedMode(): boolean {
    return this.layoutService.isAdvancedMode();
  }

  hasTableLevelResults(): boolean {
    return !!this.results?.report?.table_level;
  }

  hasColumnLevelResults(): boolean {
    return !!this.results?.report?.per_column && Object.keys(this.results.report.per_column).length > 0;
  }

  isTableLevelExpanded(): boolean {
    return this.tableLevelExpanded;
  }

  setTableLevelExpanded(expanded: boolean): void {
    this.tableLevelExpanded = expanded;
  }

  /**
   * Get table-level summary (returns the table_level object directly)
   */
  getTableLevelSummary(): any {
    return this.results?.report?.table_level;
  }

  /**
   * Get table-level artifacts/visualizations
   * Note: In ML/Deep methods, artifacts may not exist at table level
   */
  getTableLevelArtifacts(): any {
    return this.results?.report?.table_level?.artifacts;
  }

  /**
   * Get anomaly count from table level
   */
  getTableLevelAnomalyCount(): number {
    return this.results?.report?.table_level?.anomaly_count || 0;
  }

  /**
   * Get anomaly ratio from table level
   */
  getTableLevelAnomalyRatio(): number {
    return this.results?.report?.table_level?.anomaly_ratio || 0;
  }

  /**
   * Get method family from summary
   */
  getMethodFamily(): string {
    return this.results?.report?.summary?.method_family || 'unknown';
  }

  /**
   * Get methods used from summary
   */
  getMethodsUsed(): string[] {
    return this.results?.report?.summary?.methods_used || [];
  }

  /**
   * Get table-level anomaly indices
   */
  getTableLevelAnomalyIndices(): number[] {
    return this.results?.report?.table_level?.anomaly_index || [];
  }

  /**
   * Check if table-level anomalies are available for download
   */
  hasTableLevelAnomalies(): boolean {
    return this.getTableLevelAnomalyIndices().length > 0;
  }

  /**
   * Open dialog to view and download table-level anomaly indices
   */
  openAnomalyListDialog(): void {
    const anomalyIndices = this.getTableLevelAnomalyIndices();
    if (anomalyIndices.length === 0) {
      return;
    }

    // Get anomaly scores if available
    const anomalyScores = this.results?.report?.table_level?.anomaly_scores || [];

    console.log('Opening anomaly list dialog:', {
      dataSourceId: this.dataSourceId,
      hasUploadedFileData: !!this.uploadedFileData,
      hasFileUploadServiceData: !!this.fileUploadService.getUploadedData(),
      anomalyIndicesCount: anomalyIndices.length,
      anomalyScoresCount: anomalyScores.length
    });

    // Try to fetch full dataset and merge with anomalies
    // Skip datasource fetch if dataSourceId is '0' (indicates uploaded file)
    if (this.dataSourceId && this.dataSourceId !== '0') {
      // For datasource-based detection, fetch all data
      console.log('Fetching full dataset from datasource:', this.dataSourceId);
      this.dataService.getDataSourceData(this.dataSourceId).subscribe({
        next: (response) => {
          console.log('Received datasource data for table-level:', {
            isArray: Array.isArray(response),
            isString: typeof response === 'string',
            hasData: !!response?.data,
            hasDataCaps: !!response?.Data,
            responseKeys: Object.keys(response || {}).length
          });
          
          // Handle different response formats
          let fullData: any[] = [];
          
          // If response is a JSON string, parse it first
          let parsedResponse = response;
          if (typeof response === 'string') {
            try {
              parsedResponse = JSON.parse(response);
              console.log('Parsed JSON string response for table-level');
            } catch (e) {
              console.error('Failed to parse JSON string response:', e);
            }
          }
          
          if (Array.isArray(parsedResponse)) {
            fullData = parsedResponse;
          } else if (parsedResponse?.data && Array.isArray(parsedResponse.data)) {
            fullData = parsedResponse.data;
          } else if (parsedResponse?.Data && Array.isArray(parsedResponse.Data)) {
            fullData = parsedResponse.Data;
          } else if (parsedResponse && typeof parsedResponse === 'object') {
            // Response is an object with numeric keys - convert to array
            fullData = Object.values(parsedResponse);
          }
          
          console.log('Full data array length:', fullData.length);
          this.openAnomalyListDialogWithData(anomalyIndices, anomalyScores, fullData);
        },
        error: (error) => {
          console.error('Failed to fetch full dataset, showing indices only:', error);
          // Fallback to showing indices only
          this.openAnomalyListDialogWithIndicesOnly(anomalyIndices);
        }
      });
    } else {
      // For uploaded file-based detection, use in-memory data
      // Try uploadedFileData first, then fileUploadService
      let fullData = this.uploadedFileData?.data || this.uploadedFileData;
      
      if (!fullData || !Array.isArray(fullData) || fullData.length === 0) {
        console.log('uploadedFileData not available, trying fileUploadService...');
        const uploadedData = this.fileUploadService.getUploadedData();
        fullData = uploadedData?.data || uploadedData;
      }
      
      console.log('Using uploaded file data:', {
        source: this.uploadedFileData ? 'uploadedFileData' : 'fileUploadService',
        isArray: Array.isArray(fullData),
        length: fullData?.length,
        firstRow: fullData?.[0]
      });
      
      if (Array.isArray(fullData) && fullData.length > 0) {
        this.openAnomalyListDialogWithData(anomalyIndices, anomalyScores, fullData);
      } else {
        console.warn('Full data is not valid array, falling back to indices only');
        // Fallback to showing indices only
        this.openAnomalyListDialogWithIndicesOnly(anomalyIndices);
      }
    }
  }

  /**
   * Open anomaly list dialog with full row data
   */
  private openAnomalyListDialogWithData(anomalyIndices: number[], anomalyScores: number[], fullData: any[]): void {
    console.log('Opening dialog with full data:', {
      anomalyIndicesCount: anomalyIndices.length,
      anomalyScoresCount: anomalyScores.length,
      fullDataLength: fullData.length,
      firstRow: fullData[0],
      firstAnomalyIndex: anomalyIndices[0]
    });

    // Extract rows at anomaly indices and merge with scores
    const anomalyRows = anomalyIndices.map((idx, i) => {
      const row = fullData[idx] || {};
      return {
        index: idx,
        anomaly_score: anomalyScores[i],
        ...row
      };
    });

    console.log('Prepared anomaly rows:', {
      count: anomalyRows.length,
      firstRow: anomalyRows[0],
      columnKeys: Object.keys(anomalyRows[0] || {})
    });

    this.dialog.open(AnomalyListDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      data: {
        anomalyRows: anomalyRows,
        methodFamily: this.getMethodFamily(),
        totalRecords: this.results?.report?.summary?.rows_input || 0,
        isColumnLevel: false
      }
    });
  }

  /**
   * Open anomaly list dialog with indices only (legacy fallback)
   */
  private openAnomalyListDialogWithIndicesOnly(anomalyIndices: number[]): void {
    this.dialog.open(AnomalyListDialogComponent, {
      width: '700px',
      data: {
        anomalyIndices: anomalyIndices,
        methodFamily: this.getMethodFamily(),
        totalRecords: this.results?.report?.summary?.rows_input || 0,
        isColumnLevel: false
      }
    });
  }

  /**
   * Open dialog to view and download column-level anomaly examples
   */
  openColumnAnomalyDialog(columnName: string): void {
    const anomalyExamples = this.getAnomalyExamples(columnName);
    if (anomalyExamples.length === 0) {
      return;
    }

    console.log('Opening column anomaly dialog for:', columnName, 'with', anomalyExamples.length, 'examples');

    // Try to fetch full dataset and merge with anomaly examples
    if (this.dataSourceId && this.dataSourceId !== '0') {
      // For datasource-based detection, fetch all data
      console.log('Fetching full dataset from datasource for column anomalies', this.dataSourceId);
      this.dataService.getDataSourceData(this.dataSourceId).subscribe({
        next: (response) => {
          console.log('Raw API response for column anomalies:', {
            isArray: Array.isArray(response),
            isString: typeof response === 'string',
            hasData: !!response?.data,
            hasDataCaps: !!response?.Data,
            responseKeys: Object.keys(response || {}).length,
            firstItem: Array.isArray(response) ? response[0] : null,
            firstDataItem: response?.data?.[0] || response?.Data?.[0],
            responseType: typeof response,
            firstChars: typeof response === 'string' ? response.substring(0, 100) : null
          });
          
          // Handle different response formats
          let fullData: any[] = [];
          
          // If response is a JSON string, parse it first
          let parsedResponse = response;
          if (typeof response === 'string') {
            try {
              parsedResponse = JSON.parse(response);
              console.log('Parsed JSON string response:', {
                isArray: Array.isArray(parsedResponse),
                length: parsedResponse?.length
              });
            } catch (e) {
              console.error('Failed to parse JSON string response:', e);
            }
          }
          
          if (Array.isArray(parsedResponse)) {
            fullData = parsedResponse;
          } else if (parsedResponse?.data && Array.isArray(parsedResponse.data)) {
            fullData = parsedResponse.data;
          } else if (parsedResponse?.Data && Array.isArray(parsedResponse.Data)) {
            fullData = parsedResponse.Data;
          } else if (parsedResponse && typeof parsedResponse === 'object') {
            // Response is an object with numeric keys - convert to array
            fullData = Object.values(parsedResponse);
          }
          
          console.log('Extracted full data:', {
            isArray: Array.isArray(fullData),
            length: fullData?.length,
            firstRow: fullData?.[0]
          });
          this.openColumnAnomalyDialogWithData(columnName, anomalyExamples, fullData);
        },
        error: (error) => {
          console.error('Failed to fetch full dataset for column anomalies, showing examples only:', error);
          // Fallback to showing examples only (legacy)
          this.openColumnAnomalyDialogWithExamplesOnly(columnName, anomalyExamples);
        }
      });
    } else {
      // For uploaded file-based detection, use in-memory data
      let fullData = this.uploadedFileData?.data || this.uploadedFileData;
      
      if (!fullData || !Array.isArray(fullData) || fullData.length === 0) {
        console.log('uploadedFileData not available for column anomalies, trying fileUploadService...');
        const uploadedData = this.fileUploadService.getUploadedData();
        fullData = uploadedData?.data || uploadedData;
      }
      
      console.log('Using uploaded file data for column anomalies:', {
        isArray: Array.isArray(fullData),
        length: fullData?.length
      });
      
      if (Array.isArray(fullData) && fullData.length > 0) {
        this.openColumnAnomalyDialogWithData(columnName, anomalyExamples, fullData);
      } else {
        console.warn('Full data not available for column anomalies, falling back to examples only');
        // Fallback to showing examples only (legacy)
        this.openColumnAnomalyDialogWithExamplesOnly(columnName, anomalyExamples);
      }
    }
  }

  /**
   * Open column anomaly dialog with full row data
   */
  private openColumnAnomalyDialogWithData(columnName: string, anomalyExamples: any[], fullData: any[]): void {
    console.log('Merging column anomalies with full dataset:', {
      columnName,
      examplesCount: anomalyExamples.length,
      fullDataLength: fullData.length,
      firstExample: anomalyExamples[0]
    });

    // Merge anomaly examples with full row data
    const enrichedExamples = anomalyExamples.map(example => {
      const fullRow = fullData[example.index] || {};
      // Keep the original anomaly example data and add all other columns from the full row
      return {
        ...fullRow, // All columns from the full dataset
        index: example.index, // Ensure index is from anomaly example
        value: example.value, // Keep the anomaly value
        anomaly_score: example.anomaly_score, // Keep the anomaly score
        type: example.type // Keep the anomaly type
      };
    });

    console.log('Enriched examples:', {
      count: enrichedExamples.length,
      firstExample: enrichedExamples[0],
      columnKeys: Object.keys(enrichedExamples[0] || {})
    });

    this.dialog.open(AnomalyListDialogComponent, {
      width: '90%',
      maxWidth: '1400px',
      data: {
        anomalyExamples: enrichedExamples,
        methodFamily: this.getMethodFamily(),
        totalRecords: this.results?.report?.summary?.rows_input || 0,
        columnName: columnName,
        isColumnLevel: true
      }
    });
  }

  /**
   * Open column anomaly dialog with examples only (legacy fallback)
   */
  private openColumnAnomalyDialogWithExamplesOnly(columnName: string, anomalyExamples: any[]): void {
    this.dialog.open(AnomalyListDialogComponent, {
      width: '800px',
      data: {
        anomalyExamples: anomalyExamples,
        methodFamily: this.getMethodFamily(),
        totalRecords: this.results?.report?.summary?.rows_input || 0,
        columnName: columnName,
        isColumnLevel: true
      }
    });
  }

  getColumnNames(): string[] {
    if (!this.results?.report?.per_column) {
      return [];
    }
    const columns = Object.keys(this.results.report.per_column);
    
    // Sort columns: those with anomalies first
    return columns.sort((a, b) => {
      const aCount = this.getColumnResult(a)?.anomaly_count || this.getColumnAnomalySummary(a)?.anomaly_count || 0;
      const bCount = this.getColumnResult(b)?.anomaly_count || this.getColumnAnomalySummary(b)?.anomaly_count || 0;
      
      // Sort descending by anomaly count (columns with more anomalies first)
      if (bCount !== aCount) {
        return bCount - aCount;
      }
      
      // If same count, sort alphabetically
      return a.localeCompare(b);
    });
  }

  getColumnResult(columnName: string): any {
    return this.results?.report?.per_column?.[columnName];
  }

  /**
   * Check if this is a time series anomaly detection (no table_level, has artifacts.lineplot)
   */
  isTimeSeriesDetection(): boolean {
    if (!this.results?.report?.per_column) return false;
    const firstColumn = Object.values(this.results.report.per_column)[0] as any;
    return !this.hasTableLevelResults() && !!firstColumn?.artifacts?.lineplot;
  }

  /**
   * Get anomaly summary for a column (handles both formats)
   */
  getColumnAnomalySummary(columnName: string): any {
    const columnResult = this.getColumnResult(columnName);
    return columnResult?.anomaly_summary || columnResult?.anomaly_details;
  }

  /**
   * Get intensity scores for time series format
   */
  getIntensityScores(columnName: string): { p95_score?: number; p99_score?: number } | null {
    const summary = this.getColumnAnomalySummary(columnName);
    return summary?.intensity || null;
  }

  /**
   * Get tails information (for both formats)
   */
  getTailsInfo(columnName: string): { low?: number; high?: number } | null {
    const summary = this.getColumnAnomalySummary(columnName);
    return summary?.tails || null;
  }

  isColumnExpanded(columnName: string): boolean {
    return this.expandedColumns.has(columnName);
  }

  setColumnExpanded(columnName: string, expanded: boolean): void {
    if (expanded) {
      this.expandedColumns.add(columnName);
      // Initialize default X column (prefers identified_timeseries, falls back to first available column)
      if (!this.selectedXColumns.has(columnName)) {
        const defaultXCol = this.getDefaultXColumn(columnName);
        if (defaultXCol) {
          this.selectedXColumns.set(columnName, defaultXCol);
        }
      }
      // Lazy load scatter plot if not already loaded and X column is selected
      if (!this.columnChartsLoaded.has(columnName) && this.selectedXColumns.get(columnName)) {
        this.loadColumnScatterPlot(columnName);
      }
    } else {
      this.expandedColumns.delete(columnName);
    }
  }

  /**
   * Get all numeric and date columns from the datasource
   */
  getAllNumericAndDateColumns(): string[] {
    const allColumns: string[] = [];
    
    // Get columns from results summary (if available)
    if (this.results?.report?.summary) {
      const numericColumns = this.results.report.summary.numeric_columns_scanned || [];
      const dateColumns = this.results.report.summary.timeseries_columns_detected || [];
      allColumns.push(...numericColumns, ...dateColumns);
    }
    
    // Fallback: if no summary, try to extract from uploadedFileData
    if (allColumns.length === 0 && this.uploadedFileData) {
      const dataArray = this.uploadedFileData?.data || this.uploadedFileData;
      if (Array.isArray(dataArray) && dataArray.length > 0) {
        const firstRow = dataArray[0];
        Object.keys(firstRow).forEach(col => {
          const value = firstRow[col];
          // Include if it's a number or can be parsed as a date
          if (typeof value === 'number' || !isNaN(Number(value)) || !isNaN(Date.parse(value))) {
            allColumns.push(col);
          }
        });
      }
    }
    
    // Remove duplicates and return
    return [...new Set(allColumns)];
  }

  /**
   * Get available columns for X-axis selection (excluding current column)
   * Returns all numeric/date columns from datasource, not just those being checked for anomalies
   */
  getAvailableXColumns(currentColumn: string): string[] {
    const allColumns = this.getAllNumericAndDateColumns();
    return allColumns.filter(col => col !== currentColumn);
  }

  /**
   * Get default X-axis column (prefer identified_timeseries if available)
   * Falls back to first available column if no identified_timeseries exists
   */
  getDefaultXColumn(currentColumn: string): string {
    const identifiedTimeseries = this.results?.report?.identified_timeseries;
    
    // Prefer identified_timeseries if it exists and is not the current column
    if (identifiedTimeseries && identifiedTimeseries !== currentColumn) {
      return identifiedTimeseries;
    }
    
    // Otherwise, select the first available column
    const availableColumns = this.getAvailableXColumns(currentColumn);
    return availableColumns.length > 0 ? availableColumns[0] : '';
  }

  /**
   * Load scatter plot for a column
   */
  loadColumnScatterPlot(columnName: string): void {
    if (!this.dataSourceId && !this.uploadedFileData) {
      this.columnChartErrors.set(columnName, 'No data source available');
      this.columnChartsLoaded.add(columnName);
      return;
    }

    const xColumn = this.selectedXColumns.get(columnName);
    if (!xColumn) {
      // No X column selected - don't show error, just don't load
      this.columnChartsLoaded.add(columnName);
      return;
    }

    this.columnChartLoading.set(columnName, true);
    this.columnChartErrors.delete(columnName);
    this.columnChartData.delete(columnName);

    // Determine required columns
    const columns = [xColumn, columnName];

    if (this.dataSourceId) {
      // FOR DATASOURCE: Fetch raw column data and process locally
      this.dataService.getDataSourceColumns(this.dataSourceId, columns).subscribe({
        next: (rawData) => {
          this.processAndDisplayScatter(columnName, xColumn, rawData);
        },
        error: (error) => {
          console.error(`Error loading scatter plot for ${columnName}:`, error);
          this.columnChartErrors.set(columnName, 'Failed to load chart data');
          this.columnChartLoading.set(columnName, false);
          this.columnChartsLoaded.add(columnName);
        }
      });
    } else {
      // FOR UPLOADED FILE: Extract data from memory
      const dataArray = this.uploadedFileData?.data || this.uploadedFileData;
      if (!Array.isArray(dataArray) || dataArray.length === 0) {
        this.columnChartErrors.set(columnName, 'Invalid uploaded file data');
        this.columnChartLoading.set(columnName, false);
        this.columnChartsLoaded.add(columnName);
        return;
      }

      this.processAndDisplayScatter(columnName, xColumn, dataArray);
    }
  }

  /**
   * Process raw data and display scatter plot
   */
  private processAndDisplayScatter(yColumn: string, xColumn: string, rawData: any[]): void {
    try {
      // Process the raw data into scatter plot format
      const processedData = this.chartProcessingService.processChartData(
        'scatter',
        rawData,
        xColumn,
        yColumn
      );

      // Create ChartDataResponse
      this.columnChartData.set(yColumn, {
        data: processedData,
        image: undefined
      });

      this.columnChartLoading.set(yColumn, false);
      this.columnChartsLoaded.add(yColumn);
    } catch (error) {
      console.error(`Error processing scatter plot for ${yColumn}:`, error);
      this.columnChartErrors.set(yColumn, error instanceof Error ? error.message : 'Failed to process chart');
      this.columnChartLoading.set(yColumn, false);
      this.columnChartsLoaded.add(yColumn);
    }
  }

  /**
   * Handle X-axis column selection change
   */
  onXColumnChange(columnName: string, newXColumn: string): void {
    this.selectedXColumns.set(columnName, newXColumn);
    if (newXColumn) {
      // Reload chart with new X column
      this.columnChartsLoaded.delete(columnName);
      this.columnChartData.delete(columnName);
      this.columnChartErrors.delete(columnName);
      this.loadColumnScatterPlot(columnName);
    }
  }

  /**
   * Check if column has chart loaded
   */
  hasColumnChart(columnName: string): boolean {
    return this.columnChartData.has(columnName);
  }

  /**
   * Check if column chart is loading
   */
  isColumnChartLoading(columnName: string): boolean {
    return this.columnChartLoading.get(columnName) || false;
  }

  /**
   * Get column chart error
   */
  getColumnChartError(columnName: string): string | undefined {
    return this.columnChartErrors.get(columnName);
  }

  /**
   * Get anomaly examples for a column
   */
  getAnomalyExamples(columnName: string): any[] {
    const columnResult = this.getColumnResult(columnName);
    return columnResult?.examples?.anomaly_examples || [];
  }

  /**
   * Get anomaly indices for a column (for scatter plot highlighting)
   * Returns indices from top_indices in anomaly_summary, or from anomaly_examples
   */
  getColumnAnomalyIndices(columnName: string): number[] {
    const columnResult = this.getColumnResult(columnName);
    const anomalySummary = this.getColumnAnomalySummary(columnName);
    
    // Try to get from top_indices first
    if (anomalySummary?.top_indices && Array.isArray(anomalySummary.top_indices)) {
      return anomalySummary.top_indices;
    }
    
    // Fallback to extracting indices from anomaly_examples
    const anomalyExamples = columnResult?.examples?.anomaly_examples || [];
    return anomalyExamples.map((ex: any) => ex.index).filter((idx: any) => idx !== undefined);
  }

  /**
   * Check if there are anomaly examples to download
   */
  hasAnomalyExamples(columnName: string): boolean {
    return this.getAnomalyExamples(columnName).length > 0;
  }

  formatScore(value: number): string {
    // Format to 2 significant figures
    const sigFigs = value.toPrecision(2);
    // Format to 3 decimal places
    const decimals = value.toFixed(3);
    
    // Count decimal places in the sig figs result
    const sigFigsParts = sigFigs.split('.');
    const sigFigsDecimalPlaces = sigFigsParts.length > 1 ? sigFigsParts[1].length : 0;
    
    // Prefer 2 sig figs unless it gives fewer than 3 decimal places
    return sigFigsDecimalPlaces >= 3 ? sigFigs : decimals;
  }

  /**
   * Safely format a number with fixed decimal places
   * Returns 'N/A' if value is null, undefined, or not a number
   */
  formatNumber(value: any, decimalPlaces: number = 4): string {
    if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
      return 'N/A';
    }
    return value.toFixed(decimalPlaces);
  }

  /**
   * Safely format a count/integer value
   * Returns 'N/A' if value is null, undefined, or not a number
   */
  formatCount(value: any): string {
    if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
      return 'N/A';
    }
    return value.toLocaleString();
  }

  getTotalAnomalyCount(): number {
    // If table-level results exist, use that count
    if (this.results?.report?.table_level?.anomaly_count !== undefined) {
      return this.results.report.table_level.anomaly_count;
    }

    // Otherwise, sum up anomalies from all columns
    if (!this.results?.report?.per_column) {
      return 0;
    }

    let total = 0;
    Object.values(this.results.report.per_column).forEach((columnResult: any) => {
      // Handle both formats: anomaly_count directly or within anomaly_summary
      const count = columnResult?.anomaly_count || columnResult?.anomaly_summary?.anomaly_count || 0;
      total += count;
    });
    return total;
  }

  getExecutionTimeInSeconds(): string {
    const durationMs = this.results?.meta?.duration_ms;
    if (durationMs === undefined || durationMs === null) {
      return 'N/A';
    }
    const seconds = durationMs / 1000;
    return seconds.toFixed(2);
  }

  /**
   * Generate data summary from anomaly detection results
   */
  private generateDataSummary(): void {
    // Only show summary if the API returns one
    if (!this.results?.summary) {
      this.dataSummary = null;
      return;
    }

    // Get the appropriate summary based on selected view mode
    const currentViewMode = this.layoutService.getViewMode();
    const summaryContent = currentViewMode === 'advanced' 
      ? (this.results.summary.advanced || this.results.summary.basic)
      : (this.results.summary.basic || this.results.summary.advanced);
    
    if (!summaryContent || !summaryContent.bullets || !summaryContent.summary) {
      console.warn('API summary is missing required fields for current view mode');
      this.dataSummary = null;
      return;
    }

    // Convert top_facts to key_statistics format if available
    const keyStatistics = this.results.summary.top_facts?.map(([importance, fact]: [number, any]) => {
      // Determine category based on metric type
      let category = 'general';
      if (fact.metric.includes('anomaly') || fact.metric.includes('detection')) {
        category = 'detection';
      } else if (fact.metric.includes('quality') || fact.metric.includes('score')) {
        category = 'quality';
      } else if (fact.metric.includes('rows') || fact.metric.includes('columns')) {
        category = 'size';
      } else if (fact.metric.includes('rate') || fact.metric.includes('percentage')) {
        category = 'distribution';
      }

      // Create human-readable description from metric
      let description = fact.metric
        .replace('_', ' ')
        .replace(/\b\w/g, (l: string) => l.toUpperCase());

      // Add column context if applicable
      if (fact.column) {
        description = `${description} (${fact.column})`;
      }

      // Format value
      let value: string;
      if (fact.metric.includes('percentage') || fact.metric.includes('rate')) {
        value = `${fact.value.toFixed(1)}%`;
      } else if (fact.metric.includes('rows') || fact.metric.includes('columns') || fact.metric.includes('count')) {
        value = fact.value.toLocaleString();
      } else {
        value = fact.value.toString();
      }

      return {
        category: category,
        description: description,
        value: value,
        importance_score: importance,
        scope: fact.scope // Include scope from fact (TABLE or COLUMN)
      };
    }) || [];

    this.dataSummary = {
      bullet_points: summaryContent.bullets,
      key_statistics: keyStatistics,
      overall_assessment: summaryContent.summary,
      generated_at: new Date().toISOString()
    };
  }

  /**
   * Open visualise dialog for a column
   */
  openVisualiseDialog(columnName: string): void {
    // Determine if we're using a real data source or uploaded data
    const isUploadedData = this.dataSourceId === '0' || !this.dataSourceId;
    
    // Get the selected X column for this Y column (if any)
    const selectedXColumn = this.selectedXColumns.get(columnName);
    
    const dialogRef = this.dialog.open(VisualiseDialogComponent, {
      width: '90vw',
      height: '85vh',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        columnName: columnName,
        columnInfo: this.getColumnResult(columnName), // Use column result as column info
        availableColumns: this.availableColumns.map(col => col.name),
        availableColumnsMetadata: this.availableColumns.map(col => ({ field: col.name, dataType: col.type })),
        dataSourceId: isUploadedData ? undefined : this.dataSourceId,
        uploadedData: isUploadedData ? this.fileUploadService.getUploadedData()?.data : undefined,
        initialGraphType: 'scatter',
        initialSecondColumn: selectedXColumn,
        hideYAxisSelector: true, // Hide Y-axis selector from Anomaly Detection screen
        anomalyIndices: this.getColumnAnomalyIndices(columnName) // Pass anomaly indices for highlighting
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Visualise dialog was closed', result);
    });
  }

  /**
   * Handle selection change from interactive chart
   */
  onScatterSelectionChange(columnName: string, selectedData: any[]): void {
    console.log('onScatterSelectionChange called', { columnName, dataLength: selectedData.length });
    
    if (selectedData.length === 0) {
      return;
    }

    const xColumn = this.selectedXColumns.get(columnName);
    const title = xColumn 
      ? `Selected Data: ${xColumn} vs ${columnName}`
      : `Selected Data: ${columnName}`;

    console.log('Opening dialog with data:', { title, rows: selectedData.length });

    this.dialog.open(SelectedDataDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      data: {
        title,
        data: selectedData,
        columnName: xColumn,
        secondColumnName: columnName
      }
    });
  }
}
